import { TokContext, tokContexts, TokenType, tokTypes } from 'acorn'

import type {
  AnyNode,
  Expression,
  Identifier,
  Mustache,
  MustacheLiteral,
  Node,
  ParserConstructor,
  SourceLocation,
  Token
} from 'acorn'

function isNewLine(code?: number) {
  return code === 10 || code === 13 || code === 0x20_28 || code === 0x20_29
}

export const MustacheContext = new TokContext(undefined, undefined, true)

export const MustacheLiteralToken = new TokenType('MustacheLiteral')

export const MustacheStartToken = new TokenType('{{')

export const MustacheEndToken = new TokenType('}}')

export const createMustachePlugin = () => (BaseParser: ParserConstructor) => {
  return class MustacheParser extends BaseParser {
    override finishNode<T extends Node>(
      node: Record<string, any> & T,
      type: string
    ): T {
      if (
        type === 'Identifier' &&
        (node.name === 'prototype' || node.name === '__proto__')
      )
        this.raise(this.pos, 'Access prototype is not allowed')
      return super.finishNode(node, type)
    }

    override initialContext() {
      return [MustacheContext]
    }

    parseMustacheLiteral() {
      const node = this.parseLiteral(this.value)
      // @ts-ignore
      node.type = 'MustacheLiteral'
      return node
    }

    override parseSubscript(
      base: AnyNode,
      startPos: number,
      startLoc?: SourceLocation,
      noCalls?: boolean,
      ...args: any[]
    ): Expression {
      const node = super.parseSubscript(
        base,
        startPos,
        startLoc,
        noCalls,
        ...args
      )
      // 如果节点是 MemberExpression，注入 expression 属性到根标识符节点
      if (node.type === 'MemberExpression') {
        let root = node.object
        while (root.type === 'MemberExpression') {
          root = root.object
        }
        if (root.type === 'Identifier') {
          root.subchain = this.input.slice(root.end, node.end)
        }
        node.root = root as Identifier
      }

      // 如果节点是 CallExpression，补充调用语义和数据访问链
      if (node.type === 'CallExpression') {
        // 直接调用：query1()
        if (node.callee.type === 'Identifier') {
          node.callee.invoked = true
        }

        // 成员调用：query1.run() / query1.data.map()
        if (node.callee.type === 'MemberExpression') {
          let root = node.callee.object
          while (root.type === 'MemberExpression') {
            root = root.object
          }
          if (root.type === 'Identifier') {
            root.invoked = true
            // 获取从根节点到当前节点的完整路径
            const fullPath = this.input.slice(root.end, node.callee.end)
            const lastDotIndex = fullPath.lastIndexOf('.')
            // 移除最后一个方法调用名称，仅保留数据链部分
            root.subchain = fullPath.slice(0, Math.max(0, lastDotIndex))
            root.invokedMethod = fullPath.slice(lastDotIndex + 1)
          }
        }
      }

      return node
    }

    override parseTopLevel(node: Mustache) {
      node.body ??= []
      let mustache = 0
      const start = this.start
      const startLoc = this.startLoc
      while (this.type !== tokTypes.eof) {
        switch (this.type) {
          case MustacheEndToken: {
            mustache--
            this.next()

            break
          }
          case MustacheLiteralToken: {
            node.body.push(this.parseMustacheLiteral())

            break
          }
          case MustacheStartToken: {
            mustache++
            this.context = [tokContexts.b_expr]
            this.next()
            if (this.type === MustacheLiteralToken) {
              node.body.push(this.parseMustacheLiteral())
            } else if (this.type === tokTypes.eof) {
              const startPos = this.start - 2
              const startLoc = this.startLoc
              startLoc.column -= 2
              const child = this.startNodeAt(
                startPos,
                startLoc
              ) as MustacheLiteral
              child.raw = '{{'
              child.value = '{{'
              node.body.push(this.finishNode(child, 'MustacheLiteral'))
            } else {
              node.body.push(this.parseExpression())
            }
            this.context = [MustacheContext]
            this.next()

            break
          }
          // No default
        }
      }
      node.source = this.input
      if (mustache > 0) {
        const literal: MustacheLiteral = this.finishNode(
          this.startNodeAt(start, startLoc),
          'MustacheLiteral'
        )
        literal.value = this.input
        literal.raw = this.input
        node.body = [literal]
      } else {
        this.next()
      }
      return this.finishNode(node, 'Mustache')
    }

    readMustacheLiteral() {
      let out = ''
      let chunkStart = this.pos
      for (; this.pos < this.input.length; ) {
        const ch = this.input.codePointAt(this.pos)
        if (ch === 123 && this.input.codePointAt(this.pos + 1) === 123) {
          out += this.input.slice(chunkStart, this.pos)
          return this.finishToken(MustacheLiteralToken, out)
        }
        if (ch === 92) {
          // '\'
          out += this.input.slice(chunkStart, this.pos)
          out +=
            this.input.codePointAt(this.pos + 1) === 123
              ? this.readEscapedChar(false)
              : this.input[this.pos++]
          chunkStart = this.pos
        } else if (isNewLine(ch)) {
          out += this.input.slice(chunkStart, this.pos)
          ++this.pos
          if (ch === 13 && this.input.codePointAt(this.pos) === 10) ++this.pos
          if (ch === 10) out += '\n'
          out += String.fromCodePoint(ch)
          if (this.options.locations) {
            ++this.curLine
            this.lineStart = this.pos
          }
          chunkStart = this.pos
        } else {
          this.pos++
        }
      }
      out += this.input.slice(chunkStart, this.pos)
      return this.finishToken(MustacheLiteralToken, out)
    }

    override readToken(code: number): Token {
      if (this.curContext() === MustacheContext) {
        switch (code) {
          case 123: {
            if (this.input.codePointAt(this.pos + 1) === 123) {
              this.pos += 2
              return this.finishToken(MustacheStartToken)
            }
            break
          }
          case 125: {
            if (
              this.type !== MustacheEndToken &&
              this.input.codePointAt(this.pos - 1) === 125
            ) {
              this.pos++
              return this.finishToken(MustacheEndToken)
            }
            break
          }
        }
        return this.readMustacheLiteral()
      } else {
        if (
          code === 125 &&
          this.type === MustacheStartToken &&
          this.input.codePointAt(this.pos + 1) === 125
        )
          return this.finishToken(MustacheLiteralToken, '')
        return super.readToken(code)
      }
    }
  }
}
